import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Button,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClientProposalAction } from '../models/enums/ClientProposalAction';
import { ProposalWorkflowAction } from '../models/ProposalWorkflow';
import CastingSlotsInput from './CastingSlotsInput';
import RevisionRequestInput from './RevisionRequestInput';
import DeclineReasonSelector from './DeclineReasonSelector';

interface ClientActionSelectorProps {
  selectedAction: ClientProposalAction | null;
  onActionChange: (action: ClientProposalAction | null) => void;

  // Available actions from backend
  availableActions?: ProposalWorkflowAction[];

  // For casting
  castingSlots: string;
  onCastingSlotsChange: (value: string) => void;

  // For revision
  revisionMessage: string;
  onRevisionMessageChange: (value: string) => void;

  // For decline
  declineReason: number | null;
  declineReasonDetails: string;
  onDeclineReasonChange: (reason: number | null) => void;
  onDeclineReasonDetailsChange: (details: string) => void;

  // Validation errors
  errors?: {
    action?: string;
    castingSlots?: string;
    revisionMessage?: string;
    declineReason?: string;
  };

  onSubmit: () => void;
  isSubmitting?: boolean;
}

const ClientActionSelector: React.FC<ClientActionSelectorProps> = ({
  selectedAction,
  onActionChange,
  availableActions = [],
  castingSlots,
  onCastingSlotsChange,
  revisionMessage,
  onRevisionMessageChange,
  declineReason,
  declineReasonDetails,
  onDeclineReasonChange,
  onDeclineReasonDetailsChange,
  errors = {},
  onSubmit,
  isSubmitting = false
}) => {
  const { t } = useTranslation();

  // Map WorkflowActions to ClientActions
  const mapWorkflowActionToClientAction = (workflowAction: ProposalWorkflowAction): ClientProposalAction | null => {
    switch (workflowAction) {
      case ProposalWorkflowAction.RequestCasting:
        return ClientProposalAction.RequestCasting;
      case ProposalWorkflowAction.RequestRevision:
        return ClientProposalAction.RequestRevision;
      case ProposalWorkflowAction.Accept:
        return ClientProposalAction.AcceptProposal;
      case ProposalWorkflowAction.RefuseByClient:
        return ClientProposalAction.RefuseProposal;
      default:
        return null;
    }
  };

  // All possible actions
  const allActions = [
    { value: ClientProposalAction.RequestCasting, label: t('proposal.clientAction.requestCasting') },
    { value: ClientProposalAction.RequestRevision, label: t('proposal.clientAction.requestRevision') },
    { value: ClientProposalAction.AcceptProposal, label: t('proposal.clientAction.acceptProposal') },
    { value: ClientProposalAction.RefuseProposal, label: t('proposal.clientAction.refuseProposal') }
  ];

  // Filter actions based on what's available from backend
  const availableClientActions = availableActions
    .map(mapWorkflowActionToClientAction)
    .filter((action): action is ClientProposalAction => action !== null);

  const actions = allActions.filter(action => availableClientActions.includes(action.value));

  const selectedActionObj = actions.find(a => a.value === selectedAction);

  const renderActionSpecificFields = () => {
    switch (selectedAction) {
      case ClientProposalAction.RequestCasting:
        return (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('proposal.clientAction.requestCastingDescription')}
            </Typography>
            <CastingSlotsInput
              value={castingSlots}
              onChange={onCastingSlotsChange}
              error={errors.castingSlots}
            />
          </>
        );

      case ClientProposalAction.RequestRevision:
        return (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('proposal.clientAction.requestRevisionDescription')}
            </Typography>
            <RevisionRequestInput
              value={revisionMessage}
              onChange={onRevisionMessageChange}
              error={errors.revisionMessage}
              userType="client"
            />
          </>
        );

      case ClientProposalAction.AcceptProposal:
        return (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('proposal.clientAction.acceptProposalConfirmation')}
          </Alert>
        );

      case ClientProposalAction.RefuseProposal:
        return (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('proposal.clientAction.refuseProposalDescription')}
            </Typography>
            <DeclineReasonSelector
              type="client"
              reason={declineReason}
              reasonDetails={declineReasonDetails}
              onReasonChange={onDeclineReasonChange}
              onReasonDetailsChange={onDeclineReasonDetailsChange}
              error={errors.declineReason}
            />
          </>
        );

      default:
        return null;
    }
  };

  const getSubmitButtonText = () => {
    switch (selectedAction) {
      case ClientProposalAction.RequestCasting:
        return t('proposal.clientAction.submitCastingRequest');
      case ClientProposalAction.RequestRevision:
        return t('proposal.clientAction.submitRevisionRequest');
      case ClientProposalAction.AcceptProposal:
        return t('proposal.clientAction.confirmAcceptance');
      case ClientProposalAction.RefuseProposal:
        return t('proposal.clientAction.confirmRefusal');
      default:
        return t('common.submit');
    }
  };

  const getSubmitButtonColor = () => {
    switch (selectedAction) {
      case ClientProposalAction.AcceptProposal:
        return 'success';
      case ClientProposalAction.RefuseProposal:
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
        {t('proposal.clientAction.selectAction')}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Autocomplete
          options={actions}
          value={selectedActionObj || null}
          onChange={(_, newValue) => onActionChange(newValue?.value || null)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('proposal.clientAction.actionLabel')}
              error={!!errors.action}
              helperText={errors.action}
              required
            />
          )}
          fullWidth
        />
      </Box>

      {selectedAction && (
        <>
          {renderActionSpecificFields()}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color={getSubmitButtonColor()}
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {getSubmitButtonText()}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientActionSelector;
